import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation, useMatches, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { logout } from '../auth/authService';
import { displayName } from '../auth/sessionStore';
import { useSession } from '../auth/useSession';
import { toast } from '../v3/toast';
import { hasUnseenClient, sectionOf, useBadges, type BadgeState } from './badges';
import type { RouteHandle } from './GlobalToolbar';
import { initials } from './GlobalSidebar';
import { roleLabel } from './roleLabels';
import { useShellCounters } from './useShellCounters';
import { useViewport } from './viewport';
import { disablePush, enablePush, markPushAsked, pushState, setAppBadge, type PushState } from './pwa';

/**
 * Каркас ПО на телефоне и планшете (решения Ромы 05.10.2026, вариант B «Парящая»; эталон —
 * Паспорт-ПО/Claude Code/erp-mobile/app.html, опись erp-mobile/ПЕРЕДАЧА-В-БОЙ.md §1–§3):
 *   телефон  — шапка (название, стрелка «на предыдущий экран», поиск, колокольчик) и капсула из пяти значков внизу;
 *   планшет  — та же шапка и рейка значков слева;
 *   «Ещё»    — экран-меню: «Работа», «Только на компьютере», имя и «Выйти»;
 *   колокольчик — «Что нового» из тех же счётчиков, что в боковом меню (badges.ts);
 *   разделы PC_ONLY открывают экран «… — на компьютере» со ссылкой.
 * Компьютер этим файлом не пользуется (AppShell выбирает каркас по useViewport).
 */

const P: Record<string, ReactNode> = {
  today: <><circle cx="8" cy="8" r="2.6" /><path d="M8 1.5v2M8 12.5v2M1.5 8h2M12.5 8h2M3.4 3.4l1.4 1.4M11.2 11.2l1.4 1.4M3.4 12.6l1.4-1.4M11.2 4.8l1.4-1.4" /></>,
  inbox: <><path d="M2 9.5l1.8-6h8.4l1.8 6v3.5H2z" /><path d="M2 9.5h3.5l.8 1.6h3.4l.8-1.6H14" /></>,
  orders: <><rect x="3" y="2.5" width="10" height="11.5" rx="1.5" /><path d="M6 2.5v-1h4v1M5.5 6.5h5M5.5 9h5M5.5 11.5h3" /></>,
  msg: <path d="M2 3.5h12v8H8.5L5 14v-2.5H2z" />,
  qc: <><path d="M8 1.8l5 1.8v4.2c0 3-2.1 5.2-5 6.4-2.9-1.2-5-3.4-5-6.4V3.6z" /><path d="M5.8 8l1.6 1.6 2.9-3.1" /></>,
  stock: <><path d="M2 5.5L8 2.5l6 3v5l-6 3-6-3z" /><path d="M2 5.5l6 3 6-3M8 8.5v5" /></>,
  people: <><circle cx="6" cy="5.5" r="2.3" /><path d="M1.8 13c.4-2.4 2-3.7 4.2-3.7s3.8 1.3 4.2 3.7M10.6 3.4a2.2 2.2 0 010 4.2M12 9.6c1.3.5 2 1.7 2.3 3.4" /></>,
  tee: <path d="M5.5 2L2 4l1.4 2.8L5 6.2V14h6V6.2l1.6.6L14 4l-3.5-2c-.4 1-1.3 1.6-2.5 1.6S5.9 3 5.5 2z" />,
  layers: <><path d="M8 2l6 3-6 3-6-3z" /><path d="M2 8l6 3 6-3M2 11l6 3 6-3" /></>,
  gear: <><circle cx="8" cy="8" r="2" /><path d="M6.9 1.8h2.2l.4 1.7 1.3.7 1.6-.6 1.1 1.9-1.3 1.2v1.6l1.3 1.2-1.1 1.9-1.6-.6-1.3.7-.4 1.7H6.9l-.4-1.7-1.3-.7-1.6.6-1.1-1.9 1.3-1.2V7.2L2.5 6l1.1-1.9 1.6.6 1.3-.7z" /></>,
  money: <><rect x="1.8" y="4" width="12.4" height="8.5" rx="1.5" /><path d="M1.8 7h12.4M4.5 10h2.5" /></>,
  chart: <path d="M2.5 13.5V9M6.2 13.5V5.5M9.8 13.5V8M13.5 13.5V2.5" />,
  user: <><circle cx="8" cy="5.5" r="2.6" /><path d="M3 14c.5-2.8 2.4-4.2 5-4.2s4.5 1.4 5 4.2" /></>,
  search: <><circle cx="7" cy="7" r="4.2" /><path d="M10.2 10.2l3.3 3.3" /></>,
  bell: <path d="M4 11.5V7.2a4 4 0 018 0v4.3l1 1H3zM6.6 14h2.8" />,
  more: <><circle cx="4" cy="4" r="1.6" /><circle cx="12" cy="4" r="1.6" /><circle cx="4" cy="12" r="1.6" /><circle cx="12" cy="12" r="1.6" /></>,
  dots: <><circle cx="3.5" cy="8" r=".9" /><circle cx="8" cy="8" r=".9" /><circle cx="12.5" cy="8" r=".9" /></>,
  back: <path d="M10 3L5 8l5 5" />,
  chev: <path d="M6 3.5L10.5 8 6 12.5" />,
  x: <path d="M4 4l8 8M12 4l-8 8" />,
  monitor: <><rect x="1.8" y="2.5" width="12.4" height="8.5" rx="1.2" /><path d="M5.5 14h5M8 11v3" /></>,
  info: <><circle cx="8" cy="8" r="6.2" /><path d="M8 7.2v4M8 4.9v.2" /></>,
  link: <path d="M6.5 9.5l3-3M7 4.5l1.2-1.2a2.6 2.6 0 013.6 3.6L10.6 8M9 11.5l-1.2 1.2a2.6 2.6 0 01-3.6-3.6L5.4 8" />,
  trash: <><path d="M2.8 4.5h10.4M6.3 4.5V3.2h3.4v1.3M4.3 4.5l.6 8.3h6.2l.6-8.3" /><path d="M6.8 7v3.8M9.2 7v3.8" /></>,
};
export const MIcon = ({ n, className }: { n: string; className?: string }) => (
  <svg className={`m-ic${className ? ` ${className}` : ''}`} viewBox="0 0 16 16" aria-hidden="true">{P[n]}</svg>
);

interface Sec { to: string; label: string; icon: string }
/** Подписи и порядок — как в боковом меню компьютера (GlobalSidebar). */
const SEC: Record<string, Sec> = {
  today: { to: '/', label: 'Сегодня', icon: 'today' },
  inquiries: { to: '/inquiries', label: 'Заявки', icon: 'inbox' },
  orders: { to: '/orders', label: 'Заказы', icon: 'orders' },
  purchases: { to: '/purchases', label: 'Закупки', icon: 'stock' },
  messages: { to: '/messages', label: 'Сообщения', icon: 'msg' },
  qc: { to: '/qc', label: 'ОТК', icon: 'qc' },
  stock: { to: '/stock', label: 'Склад', icon: 'stock' },
  counterparties: { to: '/counterparties', label: 'Контрагенты', icon: 'people' },
  products: { to: '/products', label: 'Изделия', icon: 'tee' },
  materials: { to: '/materials', label: 'Материалы', icon: 'layers' },
  finance: { to: '/finance', label: 'Финансы', icon: 'money' },
  analytics: { to: '/analytics', label: 'Аналитика', icon: 'chart' },
  settings: { to: '/settings', label: 'Настройки', icon: 'gear' },
  me: { to: '/me', label: 'Личный кабинет', icon: 'user' },
};
const TABBAR = ['today', 'inquiries', 'orders', 'messages'];
const WORK = ['today', 'inquiries', 'orders', 'purchases', 'messages', 'qc', 'stock'];
const MORE = ['purchases', 'qc', 'stock'];
/** Решения Ромы 05.10: на телефоне и планшете эти разделы — «откройте на компьютере». */
export const PC_ONLY = ['counterparties', 'products', 'materials', 'finance', 'analytics', 'settings', 'me'];

const plural = (n: number, f: [string, string, string]): string => { const a = n % 10; const b = n % 100; return a === 1 && b !== 11 ? f[0] : a >= 2 && a <= 4 && (b < 10 || b >= 20) ? f[1] : f[2]; };
const keyOf = (pathname: string): string => { const seg = pathname.split('/')[1] ?? ''; return seg === '' ? 'today' : seg; };
/** Раздел «только на компьютере» по адресу; null — раздел работает на телефоне. */
export function pcOnlySection(pathname: string): Sec | null { const k = keyOf(pathname); return PC_ONLY.includes(k) ? SEC[k] ?? null : null; }

function countOf(k: string, b: BadgeState): number { const s = sectionOf(SEC[k]!.to); return s ? b.counts[s] ?? 0 : 0; }
function freshOf(k: string, b: BadgeState): boolean { const s = sectionOf(SEC[k]!.to); return !!s && hasUnseenClient(s, b); }
function Badge({ k, b }: { k: string; b: BadgeState }) {
  const n = countOf(k, b);
  return <>{freshOf(k, b) ? <i className="m-tab__d" aria-label="Есть новое" /> : null}{n > 0 ? <b className="m-tab__n">{n > 99 ? '99+' : n}</b> : null}</>;
}
const hintOf = (k: string, b: BadgeState): string => { const n = countOf(k, b); return n > 0 ? `${SEC[k]!.label}: ${n} ${plural(n, ['дело', 'дела', 'дел'])}${freshOf(k, b) ? ', есть новое' : ''}` : SEC[k]!.label; };

/* ── нижнее меню-капсула (телефон) ── */
export function MobileTabs({ onMore }: { onMore: () => void }) {
  const b = useBadges();
  const { pathname } = useLocation();
  const cur = keyOf(pathname);
  const inMore = !TABBAR.includes(cur);
  const moreN = MORE.reduce((a, k) => a + countOf(k, b), 0);
  return (
    <nav className="m-tab" aria-label="Основная навигация">
      {TABBAR.map((k) => (
        <NavLink key={k} to={SEC[k]!.to} end={k === 'today'} className={({ isActive }) => `m-tab__i${isActive ? ' is-on' : ''}`} aria-label={hintOf(k, b)} title={SEC[k]!.label}>
          <span className="m-tab__ic"><MIcon n={SEC[k]!.icon} /><Badge k={k} b={b} /></span>
        </NavLink>
      ))}
      <button type="button" className={`m-tab__i${inMore ? ' is-on' : ''}`} onClick={onMore} aria-label="Ещё" title="Ещё">
        <span className="m-tab__ic"><MIcon n="more" />{moreN > 0 ? <b className="m-tab__n">{moreN > 99 ? '99+' : moreN}</b> : null}</span>
      </button>
    </nav>
  );
}

/* ── рейка значков (планшет) ── */
export function MobileRail({ onMore }: { onMore: () => void }) {
  const b = useBadges();
  const { user } = useSession();
  const { pathname } = useLocation();
  const name = displayName(user);
  return (
    <nav className="m-rail" aria-label="Основная навигация">
      <div className="m-rail__brand">WG</div>
      {WORK.map((k) => (
        <NavLink key={k} to={SEC[k]!.to} end={k === 'today'} className={({ isActive }) => `m-rail__i${isActive ? ' is-on' : ''}`} aria-label={hintOf(k, b)} title={hintOf(k, b)}>
          <MIcon n={SEC[k]!.icon} /><Badge k={k} b={b} />
        </NavLink>
      ))}
      <div className="m-rail__sep" />
      <button type="button" className={`m-rail__i${PC_ONLY.includes(keyOf(pathname)) ? ' is-on' : ''}`} onClick={onMore} aria-label="Ещё" title="Ещё"><MIcon n="dots" /></button>
      {user ? <Link className="m-rail__user" to="/me" title={`${name} · ${roleLabel(user.roles ?? [])} — личный кабинет`}>{initials(name)}</Link> : null}
    </nav>
  );
}

/* ── шапка ── */
export function MobileTop({ onSearch, onBell }: { onSearch: () => void; onBell: () => void }) {
  const matches = useMatches();
  const navigate = useNavigate();
  const loc = useLocation();
  const b = useBadges();
  const handles = matches.map((m) => (m.handle as RouteHandle | undefined) ?? {}).filter((h) => h.title);
  const last = handles[handles.length - 1];
  const pc = pcOnlySection(loc.pathname);
  const title = pc?.label ?? last?.title ?? 'WearGroup';
  // Карточка (заявка, заказ): вместо названия — «‹ Заявки» к своему списку.
  // Диалог на телефоне — отдельный экран: «‹ Сообщения» (на планшете список и диалог рядом — назад не нужен).
  const vp = useViewport();
  const dialog = vp === 'phone' && loc.pathname.startsWith('/messages/') && loc.pathname.length > '/messages/'.length;
  const back = pc ? null : last?.workspaceTo ? { to: last.workspaceTo, label: last.workspace ?? 'Назад' } : dialog ? { to: '/messages', label: 'Сообщения' } : null;
  // «На предыдущий экран» — только если в ПО есть куда вернуться (react-router нумерует шаги истории).
  const canGoBack = Number((window.history.state as { idx?: number } | null)?.idx ?? 0) > 0;
  const total = (['inquiries', 'messages', 'orders', 'qc', 'stock'] as const).reduce((a, k) => a + countOf(k, b), 0);
  // Число дел — и на значке ПО на экране телефона (там, где телефон это умеет).
  useEffect(() => { if (b.loadedAt !== null) setAppBadge(total); }, [total, b.loadedAt]);
  return (
    <header className="m-top">
      {back ? <Link className="m-top__back" to={back.to}><MIcon n="back" />{back.label}</Link> : null}
      <h1 className="m-top__t">
        {back ? null : <><span>{title}</span>{canGoBack ? <button type="button" className="m-top__prev" onClick={() => navigate(-1)} aria-label="На предыдущий экран" title="На предыдущий экран"><MIcon n="back" /></button> : null}</>}
      </h1>
      {back ? null : <button type="button" className="m-btn-c" onClick={onSearch} aria-label="Поиск" title="Поиск"><MIcon n="search" /></button>}
      <button type="button" className="m-btn-c" onClick={onBell} aria-label={total ? `Что нового: ${total} ${plural(total, ['дело', 'дела', 'дел'])}` : 'Что нового'} title="Что нового">
        <MIcon n="bell" />{total > 0 ? <b className="m-badge">{total > 99 ? '99+' : total}</b> : null}
      </button>
    </header>
  );
}

/* ── шторка снизу (планшет — панель справа) и страница на весь экран (планшет — панель справа) ── */
function useEscape(onClose: () => void) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
}
export function MSheet({ title, onClose, children, foot }: { title: string; onClose: () => void; children: ReactNode; foot?: ReactNode }) {
  useEscape(onClose);
  return (
    <>
      <div className="m-scrim" onMouseDown={onClose} aria-hidden="true" />
      <section className="m-sheet" role="dialog" aria-modal="true" aria-label={title}>
        <button type="button" className="m-sheet__grip" onClick={onClose} aria-label="Закрыть" />
        <div className="m-sheet__head"><div className="m-sheet__t">{title}</div><button type="button" className="m-x" onClick={onClose} aria-label="Закрыть"><MIcon n="x" /></button></div>
        <div className="m-sheet__body">{children}</div>
        {foot ? <div className="m-sheet__foot">{foot}</div> : null}
      </section>
    </>
  );
}
export function MPage({ title, head, onClose, children }: { title: string; head: ReactNode; onClose: () => void; children: ReactNode }) {
  useEscape(onClose);
  return (
    <>
      <div className="m-scrim m-scrim--page" onMouseDown={onClose} aria-hidden="true" />
      <section className="m-page" role="dialog" aria-modal="true" aria-label={title}>
        <div className="m-page__head">{head}</div>
        <div className="m-page__body">{children}</div>
      </section>
    </>
  );
}

/* ── «Ещё»: экран-меню ── */
export function MobileMore({ onClose, phone }: { onClose: () => void; phone: boolean }) {
  const b = useBadges();
  const navigate = useNavigate();
  const { user } = useSession();
  const { pathname } = useLocation();
  const cur = keyOf(pathname);
  const name = displayName(user);
  const go = (to: string) => { onClose(); navigate(to); };
  return (
    <MPage title="Меню" onClose={onClose} head={<><div className="m-page__t">Меню</div><button type="button" className="m-x" onClick={onClose} aria-label="Закрыть"><MIcon n="x" /></button></>}>
      <div className="mm-cap">Работа</div>
      <div className="mr-list">
        {(phone ? WORK : MORE).map((k) => (
          <button key={k} type="button" className={`mr-i${k === cur ? ' is-on' : ''}`} onClick={() => go(SEC[k]!.to)}>
            <MIcon n={SEC[k]!.icon} /><span>{SEC[k]!.label}</span>
            {freshOf(k, b) ? <i className="mr-d" aria-label="Есть новое" /> : null}
            {countOf(k, b) > 0 ? <b className="mr-n">{countOf(k, b)}</b> : null}
            <MIcon n="chev" className="mr-chev" />
          </button>
        ))}
      </div>
      <div className="mm-cap">Только на компьютере</div>
      <div className="mr-list">
        {PC_ONLY.filter((k) => k !== 'me').map((k) => (
          <button key={k} type="button" className="mr-i mr-i--pc" onClick={() => go(SEC[k]!.to)}><MIcon n={SEC[k]!.icon} /><span>{SEC[k]!.label}</span><small><MIcon n="monitor" /></small></button>
        ))}
      </div>
      {user ? (
        <button type="button" className="mm-user" onClick={() => go('/me')}>
          <span className="mm-ava">{initials(name)}</span>
          <span><b>{name}</b><small>{roleLabel(user.roles ?? [])} · личный кабинет</small></span>
          <MIcon n="chev" />
        </button>
      ) : null}
      <PushRow />
      <button type="button" className="mm-out" onClick={() => { onClose(); void logout(); }}>Выйти</button>
    </MPage>
  );
}

/* ── уведомления на этом устройстве (push от ПО; решение Ромы 05.10.2026 — вдобавок к Telegram-ботам) ── */
function PushRow() {
  const [state, setState] = useState<PushState | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { let alive = true; void pushState().then((s) => { if (alive) setState(s); }); return () => { alive = false; }; }, []);
  if (state === null || state === 'unsupported') return null;
  const flip = async () => {
    if (busy) return;
    setBusy(true);
    const next = state === 'on' ? await disablePush() : await enablePush();
    setState(next); setBusy(false);
    toast(next === 'on' ? 'Уведомления на этом устройстве включены' : next === 'denied' ? 'Уведомления запрещены в настройках телефона' : 'Уведомления на этом устройстве выключены', next === 'denied' ? 'warn' : 'ok');
  };
  return (
    <>
      <div className="mm-cap">Уведомления</div>
      <div className="mr-list">
        <button type="button" className="mr-i" onClick={() => void flip()} disabled={busy || state === 'denied'}>
          <MIcon n="bell" /><span>{state === 'denied' ? 'Запрещены в настройках телефона' : 'На этом устройстве'}</span>
          {state === 'denied' ? null : <small className="mr-sw" data-on={state === 'on'} aria-label={state === 'on' ? 'включены' : 'выключены'}><i /></small>}
        </button>
      </div>
    </>
  );
}

/** Вопрос при первом запуске на телефоне или планшете: включить уведомления на этом устройстве. */
export function MobileAskPush({ onClose }: { onClose: () => void }) {
  const [busy, setBusy] = useState(false);
  const later = () => { markPushAsked(); onClose(); };
  const on = async () => {
    setBusy(true);
    const s = await enablePush();
    toast(s === 'on' ? 'Уведомления на этом устройстве включены' : s === 'denied' ? 'Уведомления запрещены в настройках телефона' : 'Уведомления не включены — можно включить в «Ещё»', s === 'on' ? 'ok' : 'warn');
    onClose();
  };
  return (
    <MSheet title="Уведомления" onClose={later} foot={<><button type="button" className="m-ghost" onClick={later} disabled={busy}>Не сейчас</button><button type="button" className="m-cta" onClick={() => void on()} disabled={busy}>Включить</button></>}>
      <p className="ms-note" style={{ color: 'var(--m-ink)' }}>Новая заявка, решение клиента по КП и сообщение из кабинета или с почты — уведомлением на этом устройстве. Переписку в Telegram вы видите сами, по ней push не идёт.</p>
    </MSheet>
  );
}

/* ── колокольчик: «Что нового» ── */
export function MobileNews({ onClose }: { onClose: () => void }) {
  const b = useBadges();
  const navigate = useNavigate();
  const { user } = useSession();
  const counters = useShellCounters(Boolean(user));
  const go = (to: string) => { onClose(); navigate(to); };
  const rows = (['inquiries', 'messages', 'orders', 'qc', 'stock'] as const).filter((k) => countOf(k, b) > 0);
  const pay = counters.paymentReminders ?? 0;
  return (
    <MSheet title="Что нового" onClose={onClose}>
      {b.loadedAt === null ? <div className="m-skel" aria-label="Загрузка"><i /><i /></div>
        : rows.length ? (
          <div className="mr-list">
            {rows.map((k) => { const n = countOf(k, b); return (
              <button key={k} type="button" className="mn-row" onClick={() => go(SEC[k]!.to)}>
                <MIcon n={SEC[k]!.icon} />
                <span><b>{SEC[k]!.label}</b><small>{n} {plural(n, ['дело', 'дела', 'дел'])} {plural(n, ['ждёт', 'ждут', 'ждут'])} вас{freshOf(k, b) ? ' · есть новое от клиента' : ''}</small></span>
                <b className="mr-n">{n}</b>
              </button>
            ); })}
          </div>
        ) : <p className="ms-note">Дел, которые ждут вас, нет.</p>}
      {pay > 0 ? (
        <>
          <div className="mm-cap">Деньги</div>
          <div className="mr-list">
            <button type="button" className="mn-row" onClick={() => go('/finance?tab=reminders')}>
              <MIcon n="money" />
              <span><b>Напоминания об оплате</b><small>{pay} {plural(pay, ['заказ отгружен и не оплачен', 'заказа отгружены и не оплачены', 'заказов отгружены и не оплачены'])} · на компьютере</small></span>
              <b className="mr-n">{pay}</b>
            </button>
          </div>
        </>
      ) : null}
    </MSheet>
  );
}

/* ── поиск экраном: клиент, контрагент или номер заказа (те же два запроса, что у поиска на компьютере) ── */
interface Hit { key: string; title: string; sub: string; href: string }
export function MobileSearch({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<Hit[]>([]);
  const [state, setState] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [again, setAgain] = useState(0);
  const input = useRef<HTMLInputElement | null>(null);
  useEffect(() => { input.current?.focus(); }, []);
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setHits([]); setState('idle'); return; }
    let alive = true;
    setState('loading');
    const timer = setTimeout(() => {
      Promise.all([
        api.query<{ items: Array<{ id: number; name: string; public_id: string }> }>('counterparties.list', { q, include_archived: true }),
        api.query<{ items: Array<{ id: number; public_id: string; client_name: string }> }>('orders.list', { q, limit: 20 }),
      ]).then(([cps, orders]) => {
        if (!alive) return;
        setHits([
          ...cps.data.items.slice(0, 8).map((c) => ({ key: `cp${c.id}`, title: c.name || '(без названия)', sub: `Контрагент · ${c.public_id}`, href: `/counterparties?kind=all&sel=${c.id}` })),
          ...orders.data.items.map((o) => ({ key: `o${o.id}`, title: o.client_name, sub: `Заказ ${o.public_id}`, href: `/orders/${o.id}` })),
        ]);
        setState('ready');
      }).catch(() => { if (alive) setState('error'); });
    }, 200);
    return () => { alive = false; clearTimeout(timer); };
  }, [query, again]);
  return (
    <MPage title="Поиск" onClose={onClose} head={<>
      <label className="ms-in"><MIcon n="search" /><input ref={input} type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Клиент, контрагент или номер заказа…" autoComplete="off" enterKeyHint="search" aria-label="Поиск" /></label>
      <button type="button" className="ms-cancel" onClick={onClose}>Отмена</button>
    </>}>
      {state === 'idle' ? <p className="ms-note">Наберите хотя бы 2 буквы: имя клиента, контрагента или номер заказа.</p> : null}
      {state === 'loading' ? <div className="m-skel" aria-label="Ищу"><i /><i /><i /></div> : null}
      {state === 'error' ? <div className="m-state"><b>Не удалось выполнить поиск</b><button type="button" className="m-btn2" onClick={() => setAgain((n) => n + 1)}>Повторить</button></div> : null}
      {state === 'ready' && hits.length === 0 ? <p className="ms-note">Ничего не найдено</p> : null}
      {state === 'ready' && hits.length ? (
        <div className="mr-list">
          {hits.map((h) => <button key={h.key} type="button" className="ms-row" onClick={() => { onClose(); navigate(h.href); }}><span><b>{h.title}</b><small>{h.sub}</small></span><MIcon n="chev" /></button>)}
        </div>
      ) : null}
    </MPage>
  );
}

/* ── экран «… — на компьютере» ── */
export function PcOnlyScreen({ section }: { section: Sec }) {
  const { user } = useSession();
  const loc = useLocation();
  const host = typeof window !== 'undefined' ? window.location.host : 'app.wrwork.ru';
  const address = `${host}${loc.pathname}`;
  const copy = async () => {
    const url = `${window.location.origin}${loc.pathname}${loc.search}`;
    try { await navigator.clipboard.writeText(url); toast('Ссылка скопирована'); }
    catch { toast(`Не удалось скопировать. Адрес: ${address}`, 'warn'); }
  };
  return (
    <div className="pc-box">
      <MIcon n="monitor" />
      <h2>{section.label} — на компьютере</h2>
      <p>{section.to === '/me' && user ? `${displayName(user)} · ${roleLabel(user.roles ?? [])}. ` : ''}На телефоне и планшете этот раздел не открывается. Откройте <code>{address}</code> на компьютере.</p>
      <button type="button" className="m-btn2" onClick={() => void copy()}><MIcon n="link" />Скопировать ссылку</button>
    </div>
  );
}
